import {
    Component,
    Injector,
    OnInit,
    Renderer2,
    ViewChild,
    computed,
    effect,
    inject,
    signal
} from '@angular/core';

import {
    $state,
    $stateParams,
    restManager,
    util
} from 'app/ajs-upgraded-providers';

import {
    BarcodeManagerService,
    ConfigurationManagerService,
    ConnectionService,
    EntityManagerService,
    EnvironmentInfoService,
    LoaderService,
    PaginatedResponse,
    RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
    RightSidenavService,
    ScreenOrientationService,
    ToolbarEventsService
} from 'src/app/core/services';

import {FilterChangedEvent, IServerSideDatasource, RowClickedEvent, ServerSideRowSelectionState, SortChangedEvent} from 'ag-grid-community';
import {DataExport, QueryPaginationHistorySales, SalesFe, TableData} from 'src/app/shared/model/model';
import {from, Observable, tap} from 'rxjs';
import {GridServerSideComponent} from 'src/app/shared/components/grid-server-side/grid-server-side.component';
import {
    GridClickableHistorySalesButtonComponent,
    GridFixedButtons,
    headersTranslate,
    ImplementServerSideGrid
} from 'src/app/shared/components/grid';
import {
    AlertDialogService,
    ConfirmDialogService,
    GridDownloadDialogService,
    IntervalBetweenTwoDatesDialogService,
    OpenDialogsService
} from 'src/app/dialogs';
import {autobind} from 'src/app/models/decorators.model';
import {GridCellFormatterService} from 'src/app/shared/components/grid/utils/grid-cell-formatter.service';
import {GridCellExportFormatterService} from 'src/app/shared/components/grid/utils/grid-cell-export-formatter.service';
import {ComponentPortal} from '@angular/cdk/portal';
import {HistorySaleDetailComponent} from '../history-sale-detail/history-sale-detail.component';
import {Customers, Sales} from 'tilby-models';
import {HistorySalesService} from '../../services/history-sales.service';
import {GridService} from '../../../../../shared/components/grid/grid.service';
import { OnDestroyService } from 'src/app/core/services/on-destroy.service';
import { HistorySummaryInvoiceViewingUiService } from 'src/app/dialogs/history-summary-invoice-viewing-ui-dialog';
import { SalesToBeInvoiced } from 'src/app/dialogs/history-summary-invoice-viewing-ui-dialog/models/sales_to_be_invoiced.model';
import { TranslateService } from "@ngx-translate/core";
import { DevLogger } from 'src/app/shared/dev-logger';
import { subscribeInComponent } from "src/app/shared/angular-utils";

import {
    CustomerUtilsService
} from 'src/app/features/customers';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedModule } from 'src/app/shared/shared.module';
import { CommonModule } from '@angular/common';
import { BarcodeComponent } from 'src/app/shared/components';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { HistoryModuleSections } from '../../../models/history.module';

@Component({
	standalone: true,
    selector: 'app-history-sales',
    templateUrl: './history_sales-showcase.component.html',
    styleUrls: ['./history_sales-showcase.component.scss'],
    imports: [SharedModule, CommonModule, BarcodeComponent],
    providers: [OnDestroyService]
})
export class HistorySalesShowcaseComponent implements OnInit, ImplementServerSideGrid {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly barcodeManagerService = inject(BarcodeManagerService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly connection = inject(ConnectionService);
    private readonly entityManager = inject(EntityManagerService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly gridCellExportFormatterService = inject(GridCellExportFormatterService);
    private readonly gridCellFormatterService = inject(GridCellFormatterService);
    private readonly gridDownloadDialogService = inject(GridDownloadDialogService);
    private readonly gridService = inject(GridService);
    private readonly historySalesService = inject(HistorySalesService);
    private readonly historySummaryInvoiceViewingUiService = inject(HistorySummaryInvoiceViewingUiService);
    private readonly intervalBetweenTwoDatesDialogService = inject(IntervalBetweenTwoDatesDialogService);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly renderer = inject(Renderer2);
    private readonly restManagerService = inject(restManager);
    private readonly rightSidenavService = inject(RightSidenavService<HistorySaleDetailComponent>);
    private readonly state = inject($state);
    private readonly stateParams = inject($stateParams);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly translateService = inject(TranslateService);
    private readonly utilService = inject(util);
    private readonly _snackBar = inject(MatSnackBar);
    private readonly loaderService = inject(LoaderService);

    loading$: Observable<boolean> = this.historySalesService.loadingSubject.asObservable();
    progress$: Observable<number> = this.historySalesService.progressSubject.asObservable();
    private readonly toolbarLayoutService = inject(ToolbarLayoutService);
    private readonly screenOrientationService = inject(ScreenOrientationService);

    protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));

    isTimerExpired: boolean = false;
    timeDifferenceMinutes!: number;
    private movColumnsSwitch = false;
    private isAdvancedFilterActive = false;
    private isMagoEnabled = this.configurationManagerService.isMagoEnabled();
    @ViewChild('appGridComponent', {static: true}) gridRef!: GridServerSideComponent;
    isSelected = false;
    selectRowUIEnabled: boolean = true;
    exportFormatterOptions?: (...any: any[])=> any;
    fixedButtons: GridFixedButtons = {
        checkbox: {visible: true, lockPosition: 'left'},
        customButton: {
            visible: true,
            cellRenderer: GridClickableHistorySalesButtonComponent,
            lockPosition: 'right',
            cellRendererParams: {
                section: 'sales' as HistoryModuleSections
            }
        }
    };
    private sales?: Sales[];
    showStoredSales = !!this.configurationManagerService.getPreference('history.show_stored_sales');
    readonly query: Readonly<QueryPaginationHistorySales> = this.showStoredSales ? {
        page: 0,
        pagination: true,
        per_page: 50,
        status_in: ['closed', 'stored'].join(','),
        notes_notin: 'Conto separato'
    } : {
        page: 0,
        pagination: true,
        per_page: 50,
        status: 'closed',
    };

    private extraFilters?: Partial<QueryPaginationHistorySales>;
    private isFirstLoad = true;
    private lastActiveQuery: any = null;
    datasource: IServerSideDatasource = {
        getRows: (params) => {
            if(this.isFirstLoad) {
                this.loaderService.show();
                this.isFirstLoad = false;
            }
            //Workaround for ids handling: if the filter is being removed, reset extraFilters
            if(!Object.keys(params.api.getFilterModel()).length) {
                this.extraFilters = undefined;
            }

            const query = {
                ...this.query,
                ...(this.extraFilters || {})
            };

            this.gridRef.getRowsForServersideDatasource(
                params,
                [query],
                (a, b?) => {
                    this.lastActiveQuery = { ...a };
                    return from(this.entityManager.sales.fetchCollectionOnline(a) as Promise<PaginatedResponse<Sales>>).pipe(
                        tap({
                            next: () => this.loaderService.hide(),
                            error: () => this.loaderService.hide()
                        })
                    );
                },
                {
                    sort_type: 'desc',
                    sort_col: 'closed_at'
                },
                'HistorySalesNormalizedData'
            );
        }
    };

    private readonly country = this.configurationManagerService.getShopCountry();

    protected expenseReportConfig = {
        template_id: parseInt(this.configurationManagerService.getPreference('cashregister.expense_report.template_id') || '')
    };

    tableData: TableData[] = [
        {
            rowData$: null,
            dataType: new SalesFe({ showStoredSales: this.showStoredSales }),
            headersTranslate: headersTranslate.history_sales_showcase,
            columnsFormatter: this.gridCellFormatterService.history_sales,
        }
    ];

    get isCountryItaly(){
        return this.country === 'IT';
    }

    constructor() {
        effect(() => {
          [
            this.isLandscape(),
          ];

          setTimeout(() => {
            this.toolbarLayoutService.updateTitleVisibility();
          }, 10)
        });

        subscribeInComponent(this.gridService.history_sales$.data, data => {
            if(this.gridRef?.gridApi) {
                this.gridRef.gridApi.setGridOption('rowClass', 'ag-row-background__unselected');
            }

            this.sales = data;
        });

        subscribeInComponent(this.gridService.history_sales$.closeDetail, () => {
            if (this.gridRef?.gridApi) {
                this.gridRef.resetUIRows();
            }
        });

        subscribeInComponent(this.toolbarEventsService.events, async (event) => {
            switch (event.type) {
                case 'toolbar.search':
                    return this.gridRef.onFilterTextBoxChanged(event.search);
                case 'grid.open_tool_panel':
                    return this.gridRef.openToolPanel(event.panel);
            }
        });
    }

    ngOnInit(): void {
        this.createToolbarButtons();
        this.setModuleTitle();
        this.toolbarLayoutService.updateTitleVisibility();
        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarIcon$.next('tune');
        this.toolbarEventsService.searchBarIconFilled$.next('tune');
        this.toolbarEventsService.searchBarAction$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.searchBarActionFilled$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.exportFormatterOptions = this.gridCellExportFormatterService.historySalesFormatter;
    }

    onGridReady() {
        // checkbox validation
        this.gridRef.agGrid.selectionChanged.subscribe(row => {
            if (row.source === 'uiSelectAllFiltered') {

                const state = row.api.getServerSideSelectionState();
        
                if (state && 'selectAll' in state) {
                    row.api.forEachNode(node => {
                        if (node.data) {
                            node.setSelected(state.selectAll, false);
                        }
                    });
                }
            }
            this.historySalesService.selectedSales = row.api.getSelectedRows();
        });
    }

    // INFO: getChildFilterInstance(0) for multi instance (filters)
    async onFirstDataRendered() {
        switch (this.state.router.globals.$current.name) {
            case 'app.new.history.customer':
                const customer = this.stateParams.customer as Customers;
                const salesToBeInvoiced = this.stateParams.salesToBeInvoicedCustomer as SalesToBeInvoiced;

                const filterInstance = await this.gridRef.gridApi?.getColumnFilterInstance('customer_name');

                filterInstance?.setModel({
                    type: 'contains',
                    filter: CustomerUtilsService.getCustomerName(customer)
                });

                this.gridRef.gridApi?.onFilterChanged();

                if (salesToBeInvoiced) {
                    this.showSummarySaleDialog(salesToBeInvoiced);
                }
                break;
            case 'app.new.history.sales': // APP-TO-APP --- tilby://history/sale/32
                if (this.stateParams.id) {
                    this.loadSaleFromId(this.stateParams.id);
                }

                if(this.stateParams.showToBeInvoiced) {
                    this.showSummarySaleDialog(this.stateParams.salesToBeInvoiced);
                }
                break;
            default:
                // handle default case if needed
                break;
        }
    }

    setModuleTitle() {
        this.toolbarEventsService.moduleTitle.next("HISTORY-SALES");
    }

    /**
     * Create buttons for the toolbar
     */
    createToolbarButtons() {
        this.toolbarEventsService.buttons$.next({
            barButtons: [
                {
                    isIt: () => this.connection.isOffline(),
                    name: 'cloud_off',
                    icon: signal('cloud_off'),
                    click: () => null
                },
                {
                    isIt: () => Object.keys(this.gridRef.gridApi?.getFilterModel() || {}).length > 0,
                    name: 'clear_filters',
                    icon: signal('filter_alt_off'),
                    click: () => this.clearGridFilters()
                },
                ...( this.environmentInfoService.isAppleMobile() ? <GeneralInputButton[]>[{
                    isIt: () => this.gridRef.gridApi?.getSelectedRows()?.length === 0,
                    name: 'search_sale_by_terminal_barcode',
                    iconType: 'symbols',
                    icon: signal('barcode'),
                    click: () => this.onBarcodeScannedAppleMobile()
                }] : []),
                ...(this.environmentInfoService.isCameraBarcodeSupported() ? <GeneralInputButton[]>[{
                    isIt: () => this.gridRef.gridApi?.getSelectedRows()?.length === 0,
                    name: 'search_sale_by_barcode',
                    icon: signal('photo_camera'),
                    click: () => this.openBarcodeAndSearch()
                }]  : []),
            ],
            panelButtons: [
                {
                    isIt: signal(true),
                    name: 'columns_panel',
                    icon: signal('view_week'),
                    label: signal('TOPBAR.ACTIONS.COLUMN_SELECTED'),
                    click: () => this.gridRef.openToolPanel('columns')
                },
                {
                    isIt: signal(true),
                    name: 'movable_columns',
                    icon: signal('drag_indicator'),
                    label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
                    click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
                },
                {
                    isIt: signal(true),
                    name: 'save_columns_position',
                    icon: signal('save'),
                    label: signal('TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE'),
                    click: () => this.gridRef.openDialogSaveColumnsPreference()
                },
                {
                    isIt: signal(true),
                    name: 'advanced_filter',
                    icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
                    label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
                    click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
                },
                {
                    isIt: signal(!['FR'].includes(this.country)),
                    name: 'show_sales_to_be_invoiced',
                    icon: signal('summarize'),
                    label: signal('TOPBAR.ACTIONS.SHOW_SALES_TO_BE_INVOICED'),
                    click: () => this.showSummarySaleDialog()
                },
                {
                    isIt: signal(this.isMagoEnabled),
                    name: 'send_to_mago_erp',
                    icon: signal('cloud_upload'),
                    label: signal('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP'),
                    click: () => {
                        if(this.gridRef.gridApi?.getSelectedRows()?.length! > 0) {
                            this.checkTimer();
                            if(!this.isTimerExpired) {
                                this.alertDialogService.openDialog({data: {messageLabel: `${this.translateService.instant('TOPBAR.ACTIONS.MAGO_MAX_MINUTES')} ${this.timeDifferenceMinutes} ${this.translateService.instant(this.timeDifferenceMinutes === 1 ?'TOPBAR.ACTIONS.MAGO_ONE_MINUTE' : 'TOPBAR.ACTIONS.MAGO_MORE_MINUTES')}`}})
                            } else {
                                this.confirmDialogService.openDialog({
                                    data: {
                                        messageLabel: [
                                            `${this.translateService.instant('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_MSG_FIRST')}`,
                                            `${this.gridRef.gridApi?.getSelectedRows()?.length}`,
                                            `${this.gridRef.gridApi?.getSelectedRows()?.length === 1 ? this.translateService.instant('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_MSG_SINGLE') : this.translateService.instant('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_MSG_MULTIPLE')}`,
                                            `${this.translateService.instant('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_MSG_SECOND')}`
                                        ].join(' ')
                                    }
                                })
                                .then(res => {
                                    if(res) {
                                        const saleIds = this.gridRef.gridApi?.getSelectedRows().map((sale) => {return sale.id})

                                        let url = 'mago/send_sale?id=';
                                        saleIds?.forEach((saleId, index) => {
                                            url += saleId
                                            if(index < saleIds.length - 1) {
                                                url += ','
                                            }
                                        })

                                        this.restManagerService.getOne(url)
                                        .then((response: any) => {
                                            if(response.message === 'ok') {
                                                this.openDialogsService.openSnackBarTilby('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_SUCCESS', 'MISC.OK', { duration: 3000 });
                                                this.startTimer();
                                            } else {
                                                this.openDialogsService.openSnackBarTilby('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_ERROR', 'MISC.OK', { duration: 3000 });
                                            }
                                        })
                                        .catch((error: any) => {
                                            DevLogger.log(error)
                                        })
                                    }
                                });
                            }
                        } else {
                            this.checkTimer();
                            if(!this.isTimerExpired) {
                                this.alertDialogService.openDialog({data: {messageLabel: `${this.translateService.instant('TOPBAR.ACTIONS.MAGO_MAX_MINUTES')} ${this.timeDifferenceMinutes} ${this.translateService.instant(this.timeDifferenceMinutes === 1 ?'TOPBAR.ACTIONS.MAGO_ONE_MINUTE' : 'TOPBAR.ACTIONS.MAGO_MORE_MINUTES')}`}})
                            } else {
                                this.intervalBetweenTwoDatesDialogService.openDialog('TOPBAR.ACTIONS.RE_SEND_SALES_TO_MAGO_ERP_TITLE', 'TOPBAR.ACTIONS.RE_SEND_SALES_TO_MAGO_ERP_MSG_FIRST', 'TOPBAR.ACTIONS.RE_SEND_SALES_TO_MAGO_ERP_MSG_SECOND')
                                .then((res) => {
                                    if(res.start_date && res.end_date) {
                                        this.restManagerService.getOne(`mago/send_sale?closed_at_since=${res.start_date}&closed_at_max=${res.end_date}`)
                                        .then((response: any) => {
                                            if(response.message === 'ok') {
                                                this.openDialogsService.openSnackBarTilby('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_SUCCESS', 'MISC.OK', { duration: 3000 });
                                                this.startTimer();
                                            } else {
                                                this.openDialogsService.openSnackBarTilby('TOPBAR.ACTIONS.SEND_TO_MAGO_ERP_ERROR', 'MISC.OK', { duration: 3000 });
                                            }
                                        })
                                        .catch((error: any) => {
                                            DevLogger.log(error)
                                        })
                                    }
                                })
                            }
                        }
                    }
                },
                {
                    isIt: () => (this.gridRef.gridApi?.getSelectedRows()?.length || 0) > 0,
                    name: 'summary_invoice',
                    icon: signal('receipt'),
                    label: signal('TOPBAR.ACTIONS.SUMMARY_INVOICE'),
                    click: () => this.historySalesService.oldSummaryInvoiceDialog()
                },
                {
                    isIt: signal(true),
                    name: 'export',
                    icon: signal('download'),
                    label: signal('TOPBAR.ACTIONS.EXPORT'),
                    click: async () => {
                        const dataToExport = this.tableData.map(tableData => ({ ...tableData, extraQueryParams: { status: 'closed' }, sortFilterQuery: this.gridRef.getSortFilterQuery(), countSelectedRows: this.gridRef.gridApi?.getSelectedRows().length }));
                        const res = await this.gridDownloadDialogService.openDialog('history_sales', { data: dataToExport }, 'sales');

                        if (res) {
                            this.onExport(res, 'sales', { status: 'closed' });
                        }
                    }
                },
                {
                    isIt: signal(!!this.configurationManagerService.isUserPermittedOptIn('history.delete_sales')),
                    name: 'delete',
                    icon: signal('delete'),
                    label: signal('TOPBAR.ACTIONS.DELETE_SALES'),
                    click: () => this.deleteSales()
                }
            ]
        });
    }

    async getSelectedRows(): Promise<Sales[]> {
        const api = this.gridRef?.gridApi;
        if (!api) return [];

        const gridState = api.getState();
        const selectionState = gridState.rowSelection as ServerSideRowSelectionState;
        const isSelectAllActive = selectionState?.selectAll === true;
        const toggledNodes = selectionState?.toggledNodes || [];
        const hasToggledNodes = toggledNodes.length > 0;

        // Selezione Manuale (isSelectAllActive è false, ma ci sono nodi selezionati)
        if (!isSelectAllActive && hasToggledNodes) {
            const allData = this.sales || [];
            return allData.filter((sale: Sales) => toggledNodes.includes(sale.uuid || ''));
        }

        // "Seleziona Tutto" o "Nessuna selezione" (ma con filtri attivi)
        if (isSelectAllActive || (!isSelectAllActive && !hasToggledNodes)) {
            try {
                this.loaderService.show();

                const requestQuery = {
                    ...(this.lastActiveQuery || {}),
                    pagination: false,
                    per_page: undefined,
                    page: undefined
                };

                const response = await this.entityManager.sales.fetchCollectionOnline(requestQuery) as Sales[];
                let results = response || [];

                if (isSelectAllActive && hasToggledNodes) {
                    results = results.filter(sale => !toggledNodes.includes(sale.uuid || ''));
                }

                return results;
            } catch (error) {
                console.error("Errore nel selezionare le righe:", error);
                return [];
            } finally {
                this.loaderService.hide();
            }
        }

        return [];
    }

    private async deleteSales() {
        // set to false advanced filter to avoid problems with the grid filtered data
        if(this.isAdvancedFilterActive === true) {
            this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = false);
        }

        const saleToDelete: Array<Sales> = await this.getSelectedRows();

        if(saleToDelete.length > 0) {
            const res = await this.historySalesService.deleteSalesMassive(saleToDelete);

            if(!res) {
                return;
            }

            if(res?.message === 'success') {
                if(this.gridRef.gridApi?.isAnyFilterPresent()) {
                    this.clearGridFilters();
                }
            }

            const isSuccess = res?.errors.length === 0;
            const msgKey = isSuccess ? 'HISTORY.DELETE_MASSIVE_SALES.SUCCESS_MSG' : 'HISTORY.DELETE_MASSIVE_SALES.ERROR_MSG';

            this._snackBar.open(this.translateService.instant(msgKey), undefined, {
                duration: 3000,
                horizontalPosition: 'left',
                verticalPosition: 'bottom',
            });

            setTimeout(() => this.gridRef.gridApi?.refreshServerSide({purge: true}), 500);
        }
    }


    clearGridFilters() {
        this.gridRef.gridApi?.setFilterModel(null);
        this.gridRef.gridApi?.onFilterChanged();
    }

    startTimer() {
        const timer = new Date(Date.now() + 5 * 60 * 1000);
        localStorage.setItem('timerFiveMinutes', timer.getTime().toString());
        this.isTimerExpired = false;
    }

    checkTimer() {
        const timestamp = localStorage.getItem('timerFiveMinutes');
        const timestampInt = timestamp ? parseInt(timestamp) : 0;
        const timer = new Date(timestampInt);

        if(Date.now() >= timer.getTime()){
            this.isTimerExpired = true;
            localStorage.removeItem('timerFiveMinutes');
        } else {
            this.timeDifferenceMinutes = Math.floor((timer.getTime() - Date.now()) / (1000 * 60))+1;
        }
    }

    async showSummarySaleDialog(salesToBeInvoiced?: SalesToBeInvoiced) {
        const res = await this.historySummaryInvoiceViewingUiService.openDialog(salesToBeInvoiced);

        if(!res) {
            return;
        }

        switch(res.action) {
            case 'summary':
                const invoiceData = res.toBeInvoiced;

                if(!invoiceData?.sales?.length) {
                    return;
                }

                const saleIds = invoiceData.sales.map(sale => sale.id);
                const response = await this.entityManager.sales.fetchCollectionOnline({ id_in: saleIds.join(','), pagination: false });

                if(Array.isArray(response)) {
                    this.historySalesService.createSummarySale(response, invoiceData.collected ? 'summary_e_rc' : 'summary_e_nrc')
                }
                break;
            case 'details':
                // Reset current filters
                this.gridRef.gridApi?.setFilterModel(null);

                // Prepare extra filter
                this.extraFilters = {
                    id_in: res.toBeInvoiced.sales.map(sale => sale.id).join(',')
                };

                await this.gridRef.gridApi?.setColumnFilterModel('summary_emitted', {
                    filterType: 'set',
                    values: ['false']
                });

                await this.gridRef.gridApi?.setColumnFilterModel('is_summary', {
                    filterType: 'set',
                    values: ['false']
                });

                this.gridRef.gridApi?.onFilterChanged();
                break;
        }
    }

//-------- NEW GENERIC TOOLBAR - END ----///

    rowClicked({data, node}: RowClickedEvent) {
        this.openDetail(data);
    }

    openDetail(data: Sales|undefined) {
        const portalInjector = Injector.create({
            providers: [{
                provide: RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
                useValue: {sale: data, gridRef: this.gridRef}
            }],
        });
        this.rightSidenavService.componentPortal$.next(new ComponentPortal(HistorySaleDetailComponent, null, portalInjector));
        this.rightSidenavService.isOpen = true;
    }

    onSortChangedEmitter($event:SortChangedEvent) {
        this.rightSidenavService.isOpen = false;
    }

    onFilterChangedEmitter($event:FilterChangedEvent) {
        this.rightSidenavService.isOpen = false;
    }

    actions({action, data, node}: any) {
        switch (action) {
            case 'detail': {
                this.openDetail(data);
                this.gridRef.onRowOrExternalClicked(node);
                break;
            }
            case 'history-sale-credit-note-cancel': {
                this.historySalesService.refundVoid(data, { gridRef: this.gridRef });
                break;
            }
            case 'history-sale-credit-print-courtesy-receipt': {
                this.historySalesService.printCourtesyReceipt(data);
                break;
            }
            case 'duplicate-sale': {
                this.historySalesService.duplicateSale(data);
                break;
            }
            case 'history-sale-restore': {
                this.historySalesService.restoreSale(data, this.gridRef);
                break;
            }
            case 'history-sale-expense-report': {
                if(this.expenseReportConfig.template_id) {
                    this.historySalesService.createExpenseReport(data, this.expenseReportConfig.template_id, this.gridRef);
                }
                break;
            }
        }
    }

    @autobind
    onExport(dataExport: DataExport,tableToCall?:string,extraQueryParams?:Pick<Sales,'status'>) {
        this.gridRef?.onBtnExport(dataExport,tableToCall,extraQueryParams);
    }

    @autobind
    private async openBarcodeAndSearch() {
        const barcodeAsSaleID = await this.barcodeManagerService.openCameraBarcode();
        this.loadSaleFromId(barcodeAsSaleID);
    }

    onBarcodeScannedAppleMobile() {
        if (this.environmentInfoService.isAppleMobile()) {
            const barcodeInput = this.renderer.selectRootElement('#barcodeInput');
            barcodeInput.focus();
            barcodeInput.value = '';
        }
    }

    onBarcodeScanned = (barcode: string) => {
        if (document.querySelectorAll('.barcode-reader').length > 1) {
            return;
        }
        this.loadSaleFromId(barcode);
    }

    private async loadSaleFromId(saleId: string) {
        const targetID = parseInt(saleId) || 0;

        let targetSale: Sales | undefined;

        //Search the sale in the sales loaded inside the grid
        if(this.sales) {
            targetSale = this.sales.find(sale => sale.id === targetID);
        }

        //Search the sale via the API
        if(!targetSale) {
            targetSale = await this.entityManager.sales.fetchOneOnline(targetID);
        }

        //Open the sale if possible
        if (targetSale && (targetSale.status === 'closed' || this.utilService.isUserAdmin())) {
            this.openDetail(targetSale);
        } else {
            this.alertDialogService.openDialog({data: {messageLabel: 'HISTORY.SALES.NOT_FOUND'}});
        }
    };
}
